import { useTranslation } from 'react-i18next';
import { AlertCircle, Loader2 } from 'lucide-react';
import { useEscapeToClose } from '@/hooks/useEscapeToClose';

interface ConfirmModalProps {
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  isPending?: boolean;
  onConfirm: () => void;
  onClose: () => void;
  type?: 'danger' | 'warning' | 'info';
  hideCancel?: boolean;
}

export default function ConfirmModal({
  title,
  message,
  confirmText,
  cancelText,
  isPending = false,
  onConfirm,
  onClose,
  type = 'danger',
  hideCancel = false,
}: ConfirmModalProps) {
  const { t } = useTranslation();
  useEscapeToClose(onClose, !isPending);

  const getTypeClasses = () => {
    switch (type) {
      case 'warning':
        return 'bg-amber-50 text-amber-600';
      case 'info':
        return 'bg-blue-50 text-blue-600';
      case 'danger':
      default:
        return 'bg-red-50 text-red-600';
    }
  };

  const getButtonClasses = () => {
    switch (type) {
      case 'warning':
        return 'bg-amber-600 hover:bg-amber-700 text-white';
      case 'info':
        return 'bg-blue-600 hover:bg-blue-700 text-white';
      case 'danger':
      default:
        return 'bg-red-600 hover:bg-red-700 text-white';
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4 animate-fade-in">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-sm overflow-hidden flex flex-col">
        <div className="p-6 text-center">
          <div className={`mx-auto w-12 h-12 rounded-full flex items-center justify-center mb-4 ${getTypeClasses()}`}>
            <AlertCircle className="w-6 h-6" />
          </div>
          <h3 className="text-lg font-bold text-gray-900 mb-2">{title}</h3>
          <p className="text-sm text-gray-500 whitespace-pre-wrap">{message}</p>
        </div>

        <div className="px-6 py-4 border-t border-gray-100 flex gap-3 bg-gray-50">
          {!hideCancel && (
            <button
              type="button"
              onClick={onClose}
              disabled={isPending}
              className="flex-1 px-4 py-2 text-gray-700 bg-white border border-gray-300 hover:bg-gray-50 rounded-lg font-medium transition-colors"
            >
              {cancelText || t('common.cancel')}
            </button>
          )}
          <button
            type="button"
            onClick={onConfirm}
            disabled={isPending}
            className={`${hideCancel ? 'w-full' : 'flex-1'} px-4 py-2 rounded-lg font-medium transition-colors flex items-center justify-center gap-2 ${getButtonClasses()}`}
          >
            {isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {confirmText || (hideCancel ? (t('confirmDelete.ok') || t('common.ok') || 'OK') : t('common.confirm'))}
          </button>
        </div>
      </div>
    </div>
  );
}
